import { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import ModalDefault from "../../ModalDefault";
import api from "../../../services/api";
import { comum } from "../../../styles/Themes/light";
import { IconClose } from "../../icons";
import {
  ButtonCloseModal,
  ModalForm,
  ModalFormScroll,
  ModalTitle,
  ModalContent,
  ModalButtonCancel,
  ModalButtonPrimary,
  ButtonRow,
  FormRow,
  FormRowThree,
  FormField,
  ToggleRow,
  ToggleTrack,
  ToggleThumb,
  ToggleLabelWrap,
  SelectWrap,
} from "./styles";
import InputDefault from "../../form/InputDefault";
import Tooltip from "../../Ui/Tooltip";
import { selectComponents, selectTheme } from "../../NovaDemanda/StepServico/InformacoesBasicas/styles";
import { tituloServicoParaExibicaoPlanos, labelAreaDireitoAudiencia } from "../../../utils/servicoMapperPlanos";
import { CivilHearing } from "../../Data/CivilHearing";
import { CriminalHearing } from "../../Data/CriminalHearing";
import { LaborHearing } from "../../Data/LaborHearing";
import { AnotherHearing } from "../../Data/AnotherHearing";

/** Mensagens de tooltip por campo bloqueado (qual campo preencher para liberar). */
const MSG_AREA_DIREITO_BLOQUEADO =
  "Preencha o campo Serviço (categoria) para liberar este campo.";
const MSG_CARREGANDO =
  "Carregando opções. Este campo será liberado em instantes.";

interface Categoria {
  id_categoria_de_servico: number;
  descricao: string;
}

interface Servico {
  id_servico: number;
  id_categoria_de_servico: number;
  titulo: string | null;
}

interface TipoAudiencia {
  id_tipo_audiencia: number;
  descricao: string;
  ordem: number | null;
}

interface Modalidade {
  id_modalidade: number;
  descricao: string;
  ordem: number | null;
}

interface Profissional {
  id_profissional: number;
  descricao: string;
  ordem: number | null;
}

interface DomainDataPreloaded {
  categorias: { id_categoria_de_servico: number; descricao: string }[];
  servicos: { id_servico: number; id_categoria_de_servico: number; titulo: string | null }[];
  tiposAudiencia: { id_tipo_audiencia: number; descricao: string; ordem: number | null }[];
  modalidades: { id_modalidade: number; descricao: string; ordem: number | null }[];
  profissionais: { id_profissional: number; descricao: string; ordem: number | null }[];
}

interface ModalNovoServicoProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  domainData?: DomainDataPreloaded | null;
}

/** RN 33.4: Retorna os tipos de audiência permitidos para a área do direito selecionada. */
function getTipoAudienciaLabelsPorArea(areaDisplay: string | null | undefined): string[] {
  if (!areaDisplay?.trim()) return [];
  const area = String(areaDisplay).toLowerCase().trim();
  if (area.includes("cível") || area.includes("civil") || area === "audiência cível")
    return CivilHearing.map((o) => o.value);
  if (area.includes("penal") || area === "audiência penal")
    return CriminalHearing.map((o) => o.value);
  if (area.includes("trabalhista") || area === "audiência trabalhista")
    return LaborHearing.map((o) => o.value);
  const outras = ["outras audiências", "outras audiencias", "outros presencial", "outros virtual"];
  if (outras.some((v) => area.includes(v)))
    return AnotherHearing.map((o) => o.value);
  return [];
}

/** Verifica se a categoria é Audiência (aceita "Audiência", "Audiencia", "Audiências", etc.). */
function categoriaIsAudiencia(descricao: string | undefined): boolean {
  if (!descricao?.trim()) return false;
  const n = descricao
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return n === "audiencia" || n === "audiencias" || n.startsWith("audiencia");
}

/** Verifica se a categoria é Diligências (aceita "Diligência", "Diligencias", "Diligências", etc.). */
function categoriaIsDiligencias(descricao: string | undefined): boolean {
  if (!descricao?.trim()) return false;
  const n = descricao
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return n === "diligencia" || n === "diligencias" || n.startsWith("diligencia");
}

/** Verifica se o título do serviço é Petições (para exclusão na criação). */
function servicoIsPeticoes(titulo: string | null | undefined): boolean {
  if (!titulo?.trim()) return false;
  const n = String(titulo)
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return n === "peticoes" || n === "peticao" || n.startsWith("peticao") || n.startsWith("peticoe");
}

const OPCOES_MODO_PRECIFICACAO = [
  { value: "fixo_em_creditos", label: "Fixo em créditos" },
  { value: "sob_consulta", label: "Sob consulta" },
];

const createSelectStyles = (hasError: boolean) => ({
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    minHeight: 48,
    height: 48,
    boxShadow: "none",
    cursor: "pointer",
    border: hasError
      ? `1px solid ${comum.colors.primaryRed400}`
      : "1px solid #EAEEF3",
    "&:hover": {
      border: hasError
        ? `1px solid ${comum.colors.primaryRed400}`
        : "1px solid #000000",
    },
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    height: 46,
    padding: "0 12px",
    display: "flex" as const,
    alignItems: "center" as const,
    flexWrap: "nowrap" as const,
  }),
  placeholder: (provided: Record<string, unknown>) => ({
    ...provided,
    color: "var(--c353a40)",
    fontSize: "14px",
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    color: "var(--c353a40)",
    fontSize: "14px",
    margin: 0,
    lineHeight: "1.2",
    padding: 0,
    alignSelf: "center" as const,
    position: "relative" as const,
    top: "auto",
    transform: "none",
  }),
  option: (provided: Record<string, unknown>) => ({
    ...provided,
    color: "var(--c5a646e)",
    fontSize: "14px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuPortal: (provided: Record<string, unknown>) => ({
    ...provided,
    zIndex: 9999,
  }),
});

export default function ModalNovoServico({ open, onClose, onSuccess, domainData }: ModalNovoServicoProps) {
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [servicos, setServicos] = useState<Servico[]>([]);
  const [tiposAudiencia, setTiposAudiencia] = useState<TipoAudiencia[]>([]);
  const [modalidades, setModalidades] = useState<Modalidade[]>([]);
  const [profissionais, setProfissionais] = useState<Profissional[]>([]);
  const [loadingDomínio, setLoadingDominio] = useState(false);

  const [idCategoria, setIdCategoria] = useState<number | null>(null);
  const [idServico, setIdServico] = useState<number | null>(null);
  /** Para Audiência: área exibida (ex. "Audiência Cível"); id_servico é resolvido com modalidade no submit. */
  const [areaDisplayDireito, setAreaDisplayDireito] = useState<string | null>(null);
  const [idTipoAudiencia, setIdTipoAudiencia] = useState<number | null>(null);
  const [idModalidade, setIdModalidade] = useState<number | null>(null);
  const [idProfissional, setIdProfissional] = useState<number | null>(null);
  const [servicoAtivo, setServicoAtivo] = useState(true);
  const [modoPrecificacao, setModoPrecificacao] = useState<string>("fixo_em_creditos");
  const [creditosPadrao, setCreditosPadrao] = useState("");
  const [descricao, setDescricao] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (domainData) {
      setCategorias(domainData.categorias as Categoria[]);
      setServicos(domainData.servicos as Servico[]);
      setTiposAudiencia(domainData.tiposAudiencia as TipoAudiencia[]);
      setModalidades(domainData.modalidades as Modalidade[]);
      setProfissionais(domainData.profissionais as Profissional[]);
      setLoadingDominio(false);
      return;
    }
    setLoadingDominio(true);
    Promise.all([
      api.get("/servico-categoria").then((r) => (r.data?.result ?? []) as Categoria[]),
      api.get("/servico").then((r) => (r.data?.result ?? []) as Servico[]),
      api.get("/servico-tipo-audiencia").then((r) => (r.data?.result ?? []) as TipoAudiencia[]),
      api.get("/servico-modalidade").then((r) => (r.data?.result ?? []) as Modalidade[]),
      api.get("/servico-profissional").then((r) => (r.data?.result ?? []) as Profissional[]),
    ])
      .then(([cat, srv, ta, mod, prof]) => {
        setCategorias(cat);
        setServicos(srv);
        setTiposAudiencia(ta);
        setModalidades(mod);
        setProfissionais(prof);
      })
      .catch(() => {
        setSubmitError("Erro ao carregar opções. Tente novamente.");
      })
      .finally(() => setLoadingDominio(false));
  }, [open, domainData]);

  const opcoesCategoria = useMemo(
    () => categorias.map((c) => ({ value: c.id_categoria_de_servico, label: c.descricao })),
    [categorias]
  );

  const servicosDaCategoria = useMemo(
    () => servicos.filter((s) => Number(s.id_categoria_de_servico) === Number(idCategoria)),
    [servicos, idCategoria]
  );

  const selectedCategoria = useMemo(
    () => categorias.find((c) => Number(c.id_categoria_de_servico) === Number(idCategoria)),
    [categorias, idCategoria]
  );
  const isAudiencia = Boolean(idCategoria != null && selectedCategoria && categoriaIsAudiencia(selectedCategoria.descricao));

  /** Para Audiência: opções únicas de área. value = display completo (para match interno); label = Civil, Penal, Trabalhista, Outros. */
  const opcoesServicoAudiencia = useMemo(() => {
    if (!isAudiencia) return [];
    const displaySet = new Set<string>();
    servicosDaCategoria.forEach((s) => {
      const display = tituloServicoParaExibicaoPlanos(s.titulo ?? "");
      if (display) displaySet.add(display);
    });
    return Array.from(displaySet)
      .sort()
      .map((value) => ({ value, label: labelAreaDireitoAudiencia(value) }));
  }, [isAudiencia, servicosDaCategoria]);

  const isDiligencias = Boolean(selectedCategoria && categoriaIsDiligencias(selectedCategoria.descricao));

  /** Para não-Audiência: lista normal por id_servico. RN: em Diligências, ocultar Petições na criação. */
  const opcoesServicoNormal = useMemo(() => {
    let lista = servicosDaCategoria;
    if (isDiligencias) {
      lista = lista.filter((s) => !servicoIsPeticoes(s.titulo));
    }
    return lista
      .map((s) => ({
        value: s.id_servico,
        label: s.titulo ?? `Serviço ${s.id_servico}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }));
  }, [servicosDaCategoria, isDiligencias]);

  /** Área normalizada para comparação (evita falha por espaços ou tipo). */
  const areaDisplayNorm = useMemo(
    () => (areaDisplayDireito != null ? String(areaDisplayDireito).trim() : ""),
    [areaDisplayDireito]
  );

  /** Resolve id_servico: para Audiência usa areaDisplayDireito + modalidade (Presencial/Virtual). */
  const idServicoResolvido = useMemo((): number | null => {
    if (isAudiencia && areaDisplayNorm && idModalidade != null) {
      const modalidadeDesc = modalidades.find((m) => m.id_modalidade === idModalidade)?.descricao?.toLowerCase() ?? "";
      const servico = servicosDaCategoria.find((s) => {
        const display = tituloServicoParaExibicaoPlanos(s.titulo ?? "").trim();
        if (display !== areaDisplayNorm) return false;
        const titulo = (s.titulo ?? "").toLowerCase();
        if (modalidadeDesc.includes("presencial") && modalidadeDesc.includes("virtual")) {
          return titulo.includes("presencial") && titulo.includes("virtual");
        }
        if (modalidadeDesc.includes("presencial")) return titulo.includes("presencial");
        if (modalidadeDesc.includes("virtual")) return titulo.includes("virtual");
        return true;
      });
      return servico?.id_servico ?? null;
    }
    if (!isAudiencia && idServico != null) return Number(idServico) || null;
    return null;
  }, [isAudiencia, areaDisplayNorm, idModalidade, modalidades, servicosDaCategoria, idServico]);

  /** RN 33.4: Tipos de audiência filtrados pela área do direito (Cível, Penal, Trabalhista, Outras). */
  const opcoesTipoAudiencia = useMemo(() => {
    const permitidos = getTipoAudienciaLabelsPorArea(areaDisplayDireito);
    if (permitidos.length === 0) return [];
    return tiposAudiencia
      .filter((t) => permitidos.includes((t.descricao ?? "").trim()))
      .map((t) => ({ value: t.id_tipo_audiencia, label: t.descricao }));
  }, [tiposAudiencia, areaDisplayDireito]);
  const opcoesModalidade = useMemo(
    () => modalidades.map((m) => ({ value: m.id_modalidade, label: m.descricao })),
    [modalidades]
  );
  const opcoesProfissional = useMemo(
    () => profissionais.map((p) => ({ value: p.id_profissional, label: p.descricao })),
    [profissionais]
  );

  const isFixo = modoPrecificacao === "fixo_em_creditos";

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (idCategoria == null) e.servico = "Informe o serviço (categoria)";
    if (isAudiencia) {
      if (idCategoria != null) {
        if (!areaDisplayNorm) e.area_direito = "Informe a área do direito (serviço)";
        else if (idServicoResolvido == null) e.modalidade = "Selecione a modalidade (Presencial/Virtual) para definir o serviço";
      }
      if (idTipoAudiencia == null) e.tipo_audiencia = "Informe o tipo de audiência";
      if (idModalidade == null && !e.modalidade) e.modalidade = "Informe a modalidade";
      if (idProfissional == null) e.profissional = "Informe o profissional";
    } else {
      if (idCategoria != null && (idServico == null || (idServico != null && Number(idServico) <= 0)))
        e.area_direito = "Informe a área do direito (serviço)";
    }
    if (!modoPrecificacao?.trim()) e.modo_precificacao = "Informe o modo de precificação";
    if (isFixo) {
      const cred = Number(creditosPadrao);
      if (!creditosPadrao?.trim() || isNaN(cred) || cred <= 0)
        e.creditos_padrao = "Créditos deve ser maior que zero.";
    }
    if (descricao.length > 200) e.descricao = "Máximo 200 caracteres.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleCriar() {
    if (!validate()) return;
    setLoading(true);
    setSubmitError(null);
    try {
      const cred = isFixo && creditosPadrao.trim() ? Number(creditosPadrao) : null;
      const idServicoEnvio = isAudiencia ? idServicoResolvido : idServico;
      if (idServicoEnvio == null) {
        setSubmitError("Não foi possível identificar o serviço. Selecione área do direito e modalidade.");
        setLoading(false);
        return;
      }
      await api.post("/plano-solicitante-template/novo-servico", {
        id_servico: idServicoEnvio,
        id_tipo_audiencia: isAudiencia ? idTipoAudiencia : null,
        id_modalidade: isAudiencia ? idModalidade : null,
        id_profissional: isAudiencia ? idProfissional : null,
        modo_precificacao: modoPrecificacao,
        creditos_padrao: cred,
        descricao: descricao.trim() || null,
        servico_ativo: servicoAtivo ? 1 : 0,
      });
      onClose();
      resetForm();
      onSuccess?.();
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { message?: string } } };
      const serverMsg = ax?.response?.data?.message?.trim();
      const msg =
        serverMsg || "Falha ao salvar o novo preço. Tente novamente.";
      setSubmitError(msg);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setIdCategoria(null);
    setIdServico(null);
    setAreaDisplayDireito(null);
    setIdTipoAudiencia(null);
    setIdModalidade(null);
    setIdProfissional(null);
    setServicoAtivo(true);
    setModoPrecificacao("fixo_em_creditos");
    setCreditosPadrao("");
    setDescricao("");
    setErrors({});
    setSubmitError(null);
  }

  function handleClose() {
    onClose();
    resetForm();
  }

  return (
    <ModalDefault
      id="modalNovoServico"
      onClose={handleClose}
      openModal={open}
      width="100%"
      maxWidth="700px"
      noScroll
      contentPadding="20px"
    >
      <ModalContent>
        <ButtonCloseModal onClick={handleClose} type="button" aria-label="Fechar">
          <IconClose color={comum.colors.interfaceGray300} />
        </ButtonCloseModal>
        <ModalTitle>
          <h1>Novo serviço</h1>
          <p>
            Preencha as informações para criar um novo serviço no template. Este serviço será
            propagado para todos os planos.
          </p>
        </ModalTitle>
        <ModalFormScroll>
          <ModalForm>
            <FormRow>
              <FormField>
                <label className="title">
                  {loadingDomínio ? (
                    <Tooltip content={MSG_CARREGANDO}>
                      <span className="label-help">
                        SERVIÇO (CATEGORIA) <span>*</span>
                      </span>
                    </Tooltip>
                  ) : (
                    <>SERVIÇO (CATEGORIA) <span>*</span></>
                  )}
                </label>
                <SelectWrap>
                  <Select<{ value: number; label: string }>
                    styles={createSelectStyles(!!errors.servico)}
                    components={selectComponents}
                    theme={selectTheme}
                    placeholder="Selecione o serviço"
                    value={opcoesCategoria.find((o) => o.value === idCategoria) ?? null}
                    onChange={(opt) => {
                      setIdCategoria(opt?.value ?? null);
                      setIdServico(null);
                      setAreaDisplayDireito(null);
                      setIdTipoAudiencia(null);
                      if (!opt || !categoriaIsAudiencia(categorias.find((c) => c.id_categoria_de_servico === opt.value)?.descricao)) {
                        setIdModalidade(null);
                        setIdProfissional(null);
                      }
                    }}
                    options={opcoesCategoria}
                    isDisabled={loadingDomínio}
                    menuPortalTarget={document.body}
                  />
                </SelectWrap>
                {errors.servico && <span className="error-msg">{errors.servico}</span>}
              </FormField>
              <FormField>
                <label className="title">
                  {idCategoria == null || loadingDomínio ? (
                    <Tooltip content={MSG_AREA_DIREITO_BLOQUEADO}>
                      <span className="label-text label-help">
                        ÁREA DO DIREITO (SERVIÇO) <span>*</span>
                      </span>
                    </Tooltip>
                  ) : (
                    <>ÁREA DO DIREITO (SERVIÇO) <span>*</span></>
                  )}
                </label>
                <SelectWrap>
                  {isAudiencia ? (
                    <Select<{ value: string; label: string }>
                      styles={createSelectStyles(!!errors.area_direito)}
                      components={selectComponents}
                      theme={selectTheme}
                      placeholder="Selecione o subtipo"
                      value={opcoesServicoAudiencia.find((o) => String(o.value).trim() === areaDisplayNorm) ?? null}
                      onChange={(opt) => {
                        setAreaDisplayDireito(opt?.value != null ? String(opt.value).trim() : null);
                        setIdTipoAudiencia(null);
                      }}
                      options={opcoesServicoAudiencia}
                      isDisabled={idCategoria == null || loadingDomínio}
                      menuPortalTarget={document.body}
                    />
                  ) : (
                    <Select<{ value: number; label: string }>
                      styles={createSelectStyles(!!errors.area_direito)}
                      components={selectComponents}
                      theme={selectTheme}
                      placeholder="Selecione o subtipo"
                      value={opcoesServicoNormal.find((o) => Number(o.value) === Number(idServico)) ?? null}
                      onChange={(opt) => setIdServico(opt?.value != null ? Number(opt.value) : null)}
                      options={opcoesServicoNormal}
                      isDisabled={idCategoria == null || loadingDomínio}
                      menuPortalTarget={document.body}
                    />
                  )}
                </SelectWrap>
                {errors.area_direito && <span className="error-msg">{errors.area_direito}</span>}
              </FormField>
            </FormRow>

            {isAudiencia && (
              <FormRowThree>
                <FormField>
                  <label className="title">
                    {loadingDomínio ? (
                      <Tooltip content={MSG_CARREGANDO}>
                        <span className="label-help">
                          TIPO DE AUDIÊNCIA <span>*</span>
                        </span>
                      </Tooltip>
                    ) : (
                      <>TIPO DE AUDIÊNCIA <span>*</span></>
                    )}
                  </label>
                  <SelectWrap>
                    <Select<{ value: number; label: string }>
                      styles={createSelectStyles(!!errors.tipo_audiencia)}
                      components={selectComponents}
                      theme={selectTheme}
                      placeholder="Selecione"
                      value={opcoesTipoAudiencia.find((o) => o.value === idTipoAudiencia) ?? null}
                      onChange={(opt) => setIdTipoAudiencia(opt?.value ?? null)}
                      options={opcoesTipoAudiencia}
                      isDisabled={loadingDomínio}
                      menuPortalTarget={document.body}
                    />
                  </SelectWrap>
                  {errors.tipo_audiencia && (
                    <span className="error-msg">{errors.tipo_audiencia}</span>
                  )}
                </FormField>
                <FormField>
                  <label className="title">
                    {loadingDomínio ? (
                      <Tooltip content={MSG_CARREGANDO}>
                        <span className="label-help">
                          MODALIDADE <span>*</span>
                        </span>
                      </Tooltip>
                    ) : (
                      <>MODALIDADE <span>*</span></>
                    )}
                  </label>
                  <SelectWrap>
                    <Select<{ value: number; label: string }>
                      styles={createSelectStyles(!!errors.modalidade)}
                      components={selectComponents}
                      theme={selectTheme}
                      placeholder="Selecione"
                      value={opcoesModalidade.find((o) => o.value === idModalidade) ?? null}
                      onChange={(opt) => setIdModalidade(opt?.value ?? null)}
                      options={opcoesModalidade}
                      isDisabled={loadingDomínio}
                      menuPortalTarget={document.body}
                    />
                  </SelectWrap>
                  {errors.modalidade && <span className="error-msg">{errors.modalidade}</span>}
                </FormField>
                <FormField>
                  <label className="title">
                    {loadingDomínio ? (
                      <Tooltip content={MSG_CARREGANDO}>
                        <span className="label-help">
                          PROFISSIONAL <span>*</span>
                        </span>
                      </Tooltip>
                    ) : (
                      <>PROFISSIONAL <span>*</span></>
                    )}
                  </label>
                  <SelectWrap>
                    <Select<{ value: number; label: string }>
                      styles={createSelectStyles(!!errors.profissional)}
                      components={selectComponents}
                      theme={selectTheme}
                      placeholder="Selecione"
                      value={opcoesProfissional.find((o) => o.value === idProfissional) ?? null}
                      onChange={(opt) => setIdProfissional(opt?.value ?? null)}
                      options={opcoesProfissional}
                      isDisabled={loadingDomínio}
                      menuPortalTarget={document.body}
                    />
                  </SelectWrap>
                  {errors.profissional && <span className="error-msg">{errors.profissional}</span>}
                </FormField>
              </FormRowThree>
            )}

            <ToggleRow className="toggle-row-readonly">
              <ToggleLabelWrap>
                <span className="label">SERVIÇO ATIVO</span>
                <span className="hint">O serviço estará disponível para uso</span>
              </ToggleLabelWrap>
              <span className="toggle-tooltip-wrap">
                <Tooltip content="Este campo não pode ser editado." side="top" centerAboveTrigger>
                  <span className="toggle-disabled-wrap" aria-hidden>
                    <ToggleTrack
                    type="button"
                    role="switch"
                    aria-checked={true}
                    $on={true}
                    disabled
                    className="toggle-disabled"
                    aria-label="Serviço ativo (somente leitura)"
                  >
                    <ToggleThumb $on={true} />
                  </ToggleTrack>
                  </span>
                </Tooltip>
              </span>
            </ToggleRow>

            {isFixo ? (
              <FormRow>
                <FormField>
                  <label className="title">
                    MODO DE PRECIFICAÇÃO <span>*</span>
                  </label>
                  <SelectWrap>
                    <Select<{ value: string; label: string }>
                      styles={createSelectStyles(!!errors.modo_precificacao)}
                      components={selectComponents}
                      theme={selectTheme}
                      placeholder="Selecione o modo de precificação"
                      value={OPCOES_MODO_PRECIFICACAO.find((o) => o.value === modoPrecificacao) ?? null}
                      onChange={(opt) => {
                        const v = opt?.value ?? "";
                        setModoPrecificacao(v);
                        if (v === "sob_consulta") setCreditosPadrao("");
                      }}
                      options={OPCOES_MODO_PRECIFICACAO}
                      menuPortalTarget={document.body}
                    />
                  </SelectWrap>
                  <span className="hint hint-block">
                    O consumo de créditos é definido previamente.
                  </span>
                  {errors.modo_precificacao && (
                    <span className="error-msg">{errors.modo_precificacao}</span>
                  )}
                </FormField>
                <FormField>
                  <label className="title">
                    CRÉDITOS PADRÃO <span>*</span>
                  </label>
                  <InputDefault
                    title=""
                    obrigatorio={false}
                    type="number"
                    value={creditosPadrao}
                    onChangeText={setCreditosPadrao}
                    placeholder="Ex: 5"
                    min="1"
                    className={errors.creditos_padrao ? "error" : ""}
                  />
                  {errors.creditos_padrao && (
                    <span className="error-msg">{errors.creditos_padrao}</span>
                  )}
                </FormField>
              </FormRow>
            ) : (
              <div className="input-full">
                <FormField>
                  <label className="title">
                    MODO DE PRECIFICAÇÃO <span>*</span>
                  </label>
                  <SelectWrap>
                    <Select<{ value: string; label: string }>
                      styles={createSelectStyles(!!errors.modo_precificacao)}
                      components={selectComponents}
                      theme={selectTheme}
                      placeholder="Selecione o modo de precificação"
                      value={OPCOES_MODO_PRECIFICACAO.find((o) => o.value === modoPrecificacao) ?? null}
                      onChange={(opt) => {
                        const v = opt?.value ?? "";
                        setModoPrecificacao(v);
                        if (v === "sob_consulta") setCreditosPadrao("");
                      }}
                      options={OPCOES_MODO_PRECIFICACAO}
                      menuPortalTarget={document.body}
                    />
                  </SelectWrap>
                  {errors.modo_precificacao && (
                    <span className="error-msg">{errors.modo_precificacao}</span>
                  )}
                </FormField>
              </div>
            )}

            <div className="input-full">
              <label className="title">DESCRIÇÃO (opcional, limite 200 caracteres)</label>
              <textarea
                placeholder="Descrição do serviço..."
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                maxLength={200}
                rows={3}
                className={errors.descricao ? "error" : ""}
              />
              {errors.descricao && (
                <span className="error-msg">{errors.descricao}</span>
              )}
            </div>

            {loadingDomínio && (
              <span className="hint hint-loading">
                Carregando opções...
              </span>
            )}
            {submitError && (
              <span className="error-msg error-msg-block">
                {submitError}
              </span>
            )}

            <ButtonRow>
              <ModalButtonCancel type="button" onClick={handleClose}>
                Cancelar
              </ModalButtonCancel>
              <ModalButtonPrimary
                type="button"
                onClick={handleCriar}
                disabled={loading || loadingDomínio}
              >
                {loading ? "Criando..." : "Criar Serviço"}
              </ModalButtonPrimary>
            </ButtonRow>
          </ModalForm>
        </ModalFormScroll>
      </ModalContent>
    </ModalDefault>
  );
}
